import { Favorite, TrackboardColumn } from "../../amplify/appsync/query";
import { PatientVisit } from "../../amplify/appsync/search-patients";
import { getRandomFirstName, getRandomLastName } from "../../utils/demo";
import { getHourMinuteDifference, getLatestTime } from "../../utils/time";
import { UserRole } from "../../utils/user";

export const DEFAULT_COLUMNS = [
    {
        name: "",
        size: 1,
        id: "reviewed",
    },
    {
        name: "Bed",
        id: "bed",
        size: 2,
    },
    {
        name: "Name",
        id: "name",
        size: 4,
    },
    {
        name: "Languages",
        id: "languages",
        size: 2,
    },
    {
        name: "Age",
        id: "age",
        size: 1,
    },
    {
        name: "Sex",
        id: "gender",
        size: 1,
    },
    {
        name: "Acuity",
        id: "triage_acuity",
        size: 3,
    },
    {
        name: "CC",
        id: "chief_complaint",
        size: 4,
    },
    {
        name: "Dispo",
        id: "ed_dispo",
        size: 3,
    },
    // {
    //     name: "Admit Risk",
    //     id: "admit_score",
    //     size: 2,
    // },
    {
        name: "Revisit Risk",
        id: "epic_revisit_score",
        size: 2,
    },
    {
        name: "AFib",
        id: "afib_present",
        size: 2,
    },
    {
        name: "No Prev Arrhythmia/Anti-Coag",
        id: "afib_no_prev",
        size: 2,
    },
    {
        name: "CHA2DS2–VASc",
        id: "afib_cha",
        size: 2,
    },
    {
        name: "Elapsed",
        id: "elapsed",
        size: 2,
    },
    {
        name: "HR",
        id: "triage_hr",
        size: 1,
    },
    {
        name: "RR",
        id: "triage_rr",
        size: 1,
    },
    {
        name: "Temp",
        id: "triage_temp",
        size: 1,
    },
    {
        name: "SpO2",
        id: "triage_spo2",
        size: 1,
    },
    {
        name: "NBP",
        id: "triage_nbp",
        size: 1,
    },
    {
        name: "",
        id: "action",
        size: 2,
    },
];

export const getDefaultColumns = (role: string, colsFromSettings?: TrackboardColumn[]) => {
    let cols: any;
    if (role !== UserRole.ADMIN) {
        // Don't show any risk scores
        cols = DEFAULT_COLUMNS.filter((col: any) => col.id !== "epic_revisit_score");
    } else {
        cols = DEFAULT_COLUMNS;
    }
    const idToCol: any = {};
    if (colsFromSettings) {
        for (let col of colsFromSettings) {
            idToCol[col.id] = col;
        }
        const newCols = [];
        for (let col of cols) {
            if (idToCol[col.id]) {
                newCols.push({
                    ...col,
                });
            }
        }
        cols = newCols;
    }
    return cols;
};

export const DEFAULT_COLUMNS_V2 = [
    {
        name: "Bed",
        id: "bed",
        size: 2,
        show: true,
    },
    {
        name: "Name",
        id: "name",
        size: 4,
        show: true,
    },
    {
        name: "Languages",
        id: "languages",
        size: 2,
        show: false,
    },
    {
        name: "Age",
        id: "age",
        size: 1,
        show: true,
    },
    {
        name: "Sex",
        id: "gender",
        size: 1,
        show: true,
    },
    {
        name: "Acuity",
        id: "triage_acuity",
        size: 3,
        show: true,
    },
    {
        name: "CC",
        id: "chief_complaint",
        size: 4,
        show: true,
    },
    {
        name: "Dispo",
        id: "ed_dispo",
        size: 3,
        show: true,
    },
    {
        name: "Revisit Risk",
        id: "epic_revisit_score",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Admit Risk (Triage)",
        id: "admit_triage",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Admit Risk (Rooming)",
        id: "admit_rooming",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Admit Risk (Dispo)",
        id: "admit_dispo",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Bounceup Risk (24h)",
        id: "bounceup_24h",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Bounceup Risk (72h)",
        id: "bounceup_72h",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Revisit Risk (30d)",
        id: "revisit_30d",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Revisit Risk (3d)",
        id: "revisit_3d",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Mortality Risk (180d)",
        id: "death_180d",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "AFib",
        id: "afib_present",
        size: 2,
        show: true,
    },
    {
        name: "No Prev Arrhythmia/Anti-Coag",
        id: "afib_no_prev",
        size: 2,
        show: true,
    },
    {
        name: "CHA2DS2-VASc",
        id: "afib_cha",
        size: 2,
        show: true,
    },
    {
        name: "Elapsed",
        id: "elapsed",
        size: 2,
        show: true,
    },
    {
        name: "HR",
        id: "triage_hr",
        size: 1,
        show: false,
    },
    {
        name: "RR",
        id: "triage_rr",
        size: 1,
        show: false,
    },
    {
        name: "Temp",
        id: "triage_temp",
        size: 1,
        show: false,
    },
    {
        name: "SpO2",
        id: "triage_spo2",
        size: 1,
        show: false,
    },
    {
        name: "NBP",
        id: "triage_nbp",
        size: 1,
        show: false,
    },
    {
        name: "Suitability",
        id: "suitability",
        size: 2,
        show: false,
    },
    {
        name: "",
        id: "action",
        size: 3,
        show: true,
    },
];

export const DEFAULT_SEARCH_COLUMNS_V2 = [
    {
        name: "Arrival",
        id: "arrival_time",
        size: 2,
        show: true,
    },
    {
        name: "Name",
        id: "name",
        size: 4,
        show: true,
    },
    {
        name: "Languages",
        id: "languages",
        size: 2,
        show: false,
    },
    {
        name: "Age",
        id: "age",
        size: 1,
        show: true,
    },
    {
        name: "Sex",
        id: "gender",
        size: 1,
        show: true,
    },
    {
        name: "Acuity",
        id: "triage_acuity",
        size: 3,
        show: true,
    },
    {
        name: "CC",
        id: "chief_complaint",
        size: 4,
        show: true,
    },
    {
        name: "Dispo",
        id: "ed_dispo",
        size: 3,
        show: true,
    },
    {
        name: "Revisit Risk",
        id: "epic_revisit_score",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Admit Risk (Triage)",
        id: "admit_triage",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Admit Risk (Rooming)",
        id: "admit_rooming",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Admit Risk (Dispo)",
        id: "admit_dispo",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Bounceup Risk (24h)",
        id: "bounceup_24h",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Bounceup Risk (72h)",
        id: "bounceup_72h",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Revisit Risk (30d)",
        id: "revisit_30d",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Revisit Risk (3d)",
        id: "revisit_3d",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "Mortality Risk (180d)",
        id: "death_180d",
        size: 2,
        show: false,
        round: 3,
    },
    {
        name: "AFib",
        id: "afib_present",
        size: 2,
        show: true,
    },
    {
        name: "No Prev Arrhythmia/Anti-Coag",
        id: "afib_no_prev",
        size: 2,
        show: true,
    },
    {
        name: "CHA2DS2-VASc",
        id: "afib_cha",
        size: 2,
        show: true,
    },
    {
        name: "Elapsed",
        id: "elapsed",
        size: 2,
        show: true,
    },
    {
        name: "HR",
        id: "triage_hr",
        size: 1,
        show: false,
    },
    {
        name: "RR",
        id: "triage_rr",
        size: 1,
        show: false,
    },
    {
        name: "Temp",
        id: "triage_temp",
        size: 1,
        show: false,
    },
    {
        name: "SpO2",
        id: "triage_spo2",
        size: 1,
        show: false,
    },
    {
        name: "NBP",
        id: "triage_nbp",
        size: 1,
        show: false,
    },
    {
        name: "Evaluated",
        id: "has_evaluate_capacity_output",
        size: 2,
        show: true,
    },
    {
        name: "",
        id: "action",
        size: 3,
        show: true,
    },
];

export const getDefaultColumnsV2 = (
    role: string,
    colsFromSettings?: TrackboardColumn[],
    showOnly?: boolean,
    zone?: string,
) => {
    const baseColumns = DEFAULT_COLUMNS_V2.map((col: any) => ({ ...col }));

    let cols: any[];
    if (role !== UserRole.ADMIN) {
        // Don't show any risk scores or suitability by default for non-admin users
        cols = baseColumns.filter((col: any) => (col.id !== "epic_revisit_score"));
    } else {
        cols = baseColumns;
    }

    const suitabilityCol = cols.find(col => col.id === "suitability");
    if (suitabilityCol) {
        if (zone === "Lobby") {
            suitabilityCol.show = true;
        } else {
            suitabilityCol.show = false;
        }
    }

    if (colsFromSettings && colsFromSettings.length > 0) {
        const idToCol = new Map<string, TrackboardColumn>();
        for (let col of colsFromSettings) {
            idToCol.set(col.id, col);
        }
        cols = cols.map((col: any) => {
            if (idToCol.has(col.id)) {
                const override = idToCol.get(col.id)!;
                return {
                    ...col,
                    show: override.show ?? true,
                };
            }
            return {
                ...col,
                show: false,
            };
        });
    }

    if (showOnly) {
        cols = cols.filter((c: any) => c.show);
    }
    return cols;
};

export const AFIB_COLUMNS = [
    {
        name: "",
        size: 1,
        id: "reviewed",
    },
    {
        name: "Name",
        id: "name",
        size: 4,
    },
    {
        name: "Arrival",
        id: "arrival_time",
        size: 2,
    },
    {
        name: "Age",
        id: "age",
        size: 1,
    },
    {
        name: "Sex",
        id: "gender",
        size: 1,
    },
    {
        name: "Acuity",
        id: "triage_acuity",
        size: 3,
    },
    {
        name: "CC",
        id: "chief_complaint",
        size: 4,
    },
    {
        name: "Dispo",
        id: "ed_dispo",
        size: 3,
    },
    {
        name: "No Prev Arrhythmia/Anti-Coag",
        id: "afib_no_prev",
        size: 2,
    },
    {
        name: "CHA2DS2–VASc",
        id: "afib_cha",
        size: 2,
    },
    {
        name: "Total Monitoring Time",
        id: "total_mon_time",
        size: 2,
    },
    {
        name: "Longest AFIB Episode",
        id: "longest_afib_episode",
        size: 2,
    },
    {
        name: "AFIB Burden",
        id: "afib_burden",
        size: 2,
    },
    {
        name: "",
        id: "action",
        size: 2,
    },
];

export const sortPatientsByCol = (
    pts: any[],
    col: string,
    asc: boolean,
    tenant: string
) => {
    if (asc) {
        pts.sort((a: any, b: any) => {
            if (col === "name") {
                const aName = a["family"] + ", " + a["given"];
                const bName = b["family"] + ", " + b["given"];
                return aName.localeCompare(bName, "en", { numeric: true });
            } else if (col === "suitability") {
                const aName = a["suitable_vertical"] + " " + a["suitabile_video"];
                const bName = b["suitable_vertical"] + " " + b["suitabile_video"];
                return aName.localeCompare(bName, "en", { numeric: true });
            } else if (col === "elapsed") {
                const aTime = `${getHourMinuteDifference(
                    new Date(a["arrival_time"] as string),
                    new Date(getLatestTime(a, tenant))
                )}`;
                const bTime = `${getHourMinuteDifference(
                    new Date(b["arrival_time"] as string),
                    new Date(getLatestTime(b, tenant))
                )}`;
                return aTime.localeCompare(bTime, "en", { numeric: true });
            } else if (
                typeof a[col] === "number" ||
                typeof b[col] === "number"
            ) {
                const aVal = a[col] === null ? 0 : a[col];
                const bVal = b[col] === null ? 0 : b[col];
                return aVal - bVal;
            } else if (
                typeof a[col] === "object" ||
                typeof b[col] === "object"
            ) {
                const aVal = a[col] && a[col][0] ? a[col][0] : 0;
                const bVal = b[col] && b[col][0] ? b[col][0] : 0;
                return aVal - bVal;
            } else {
                const aVal = a[col] ?? "";
                const bVal = b[col] ?? "";
                return aVal.localeCompare(bVal, "en", { numeric: true });
            }
        });
    } else {
        pts.sort((a: any, b: any) => {
            if (col === "name") {
                const aName = a["family"] + ", " + a["given"];
                const bName = b["family"] + ", " + b["given"];
                return bName.localeCompare(aName, "en", { numeric: true });
            } else if (col === "suitability") {
                const aName = a["suitable_vertical"] + " " + a["suitabile_video"];
                const bName = b["suitable_vertical"] + " " + b["suitabile_video"];
                return bName.localeCompare(aName, "en", { numeric: true });
            } else if (col === "elapsed") {
                const aTime = `${getHourMinuteDifference(
                    new Date(a["arrival_time"] as string),
                    new Date(getLatestTime(a, tenant))
                )}`;
                const bTime = `${getHourMinuteDifference(
                    new Date(b["arrival_time"] as string),
                    new Date(getLatestTime(b, tenant))
                )}`;
                return bTime.localeCompare(aTime, "en", { numeric: true });
            } else if (
                typeof a[col] === "number" ||
                typeof b[col] === "number"
            ) {
                const aVal = a[col] === null ? 0 : a[col];
                const bVal = b[col] === null ? 0 : b[col];
                return bVal - aVal;
            } else if (
                typeof a[col] === "object" ||
                typeof b[col] === "object"
            ) {
                const aVal = a[col] && a[col][0] ? a[col][0] : 0;
                const bVal = b[col] && b[col][0] ? b[col][0] : 0;
                return bVal - aVal;
            } else {
                const aVal = a[col] ?? "";
                const bVal = b[col] ?? "";
                return bVal.localeCompare(aVal, "en", { numeric: true });
            }
        });
    }
    return pts;
};

export const getDemoNameObj = () => {
    return {
        given: getRandomFirstName(),
        family: getRandomLastName(),
        email: "test@example.com",
        phone_mobile: "604-111-1111",
        phone_home: "604-111-1111",
        languages: ["English"],
    };
};

export const getDemoMrnToName = (patients: any) => {
    const mrnToName: any = {};
    for (let patient of patients) {
        mrnToName[patient.mrn] = getDemoNameObj();
    }
    return mrnToName;
};

export const sortPatientsDefault = (patients: PatientVisit[]) => {
    patients.sort(function (a, b) {
        const bedA = a.bed || "";
        const bedB = b.bed || "";
        const bedComparison = bedA.localeCompare(bedB);
        if (bedComparison !== 0) {
            return bedComparison;
        }

        return a.arrival_time.localeCompare(b.arrival_time);
    });
};

export const formatCsnToFavorites = (
    map: any,
    csn: string,
    favoriteType: string,
    favorite?: Favorite
) => {
    if (!(csn in map)) {
        map[csn] = [];
    }
    if (favorite) {
        // newly saved favorite
        map[csn] = map[csn].filter(
            (v: Favorite) => v.favorite_type !== favorite.favorite_type
        );
        map[csn].push(favorite);
    } else {
        // favorite deleted
        map[csn] = map[csn].filter((v: Favorite) => {
            console.log(
                "Filtering...",
                v,
                v.favorite_type,
                favoriteType,
                v.favorite_type !== favoriteType
            );
            return v.favorite_type !== favoriteType;
        });

        console.log("updateCsnToFavorites deleted", favoriteType, map);
    }
};

export const updatePatientWithMrnToName = (
    patient: PatientVisit,
    mrnToName: any
) => {
    return {
        ...patient,
        given: mrnToName[patient.mrn] ? mrnToName[patient.mrn].given : null,
        family: mrnToName[patient.mrn] ? mrnToName[patient.mrn].family : null,
        email: mrnToName[patient.mrn] ? mrnToName[patient.mrn].email : null,
        phone_mobile: mrnToName[patient.mrn]
            ? mrnToName[patient.mrn].phone_mobile
            : null,
        phone_home: mrnToName[patient.mrn]
            ? mrnToName[patient.mrn].phone_home
            : null,
        languages: mrnToName[patient.mrn]
            ? mrnToName[patient.mrn].languages
            : null,
    };
};

export const getDefaultAugmentedPatient = (p: PatientVisit) => {
    return {
        ...p,
        given: null,
        family: null,
        email: null,
        phone_mobile: null,
        phone_home: null,
        languages: [],
    };
};
